import React, { useState } from 'react';
import { MessageCircle, X, Send } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ChatBot = ({ isOpen, onClose }) => {
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: '¡Hola! Soy el asistente virtual de Zenex. ¿En qué puedo ayudarte hoy? Puedo responder preguntas sobre instalaciones solares, precios, financiamiento y más.'
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const response = await axios.post(`${API}/chat`, {
        message: userMessage
      });
      
      setMessages(prev => [...prev, { role: 'assistant', content: response.data.response }]);
    } catch (error) {
      console.error('Error en chat:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Disculpa, tuve un problema al procesar tu mensaje. ¿Podrías intentar de nuevo?' 
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed bottom-24 right-6 w-96 max-w-[calc(100vw-3rem)] bg-white rounded-2xl shadow-2xl border border-zinc-200 z-50 flex flex-col" style={{ height: '600px', maxHeight: '80vh' }}>
      {/* Header */}
      <div className="bg-gradient-to-r from-[#90EE90] to-[#7CDF7C] p-4 rounded-t-2xl flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="bg-white p-2 rounded-full">
            <img 
              src="https://customer-assets.emergentagent.com/job_9f79b292-d9d5-4e5e-9601-bb175f5ef285/artifacts/3g9iwewq_11.png"
              alt="Zenex AI"
              className="w-8 h-8"
            />
          </div>
          <div>
            <h3 className="font-bold text-zinc-900">Asistente Zenex</h3>
            <p className="text-xs text-zinc-700">Siempre en línea</p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="text-zinc-900 hover:text-zinc-700 transition-colors"
        >
          <X size={24} />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((msg, idx) => (
          <div
            key={idx}
            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[80%] px-4 py-3 rounded-2xl ${
                msg.role === 'user'
                  ? 'bg-[#90EE90] text-zinc-900'
                  : 'bg-zinc-100 text-zinc-900'
              }`}
            >
              <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-zinc-100 px-4 py-3 rounded-2xl">
              <div className="flex gap-1">
                <div className="w-2 h-2 bg-zinc-400 rounded-full animate-bounce" style={{ animationDelay: '0s' }}></div>
                <div className="w-2 h-2 bg-zinc-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                <div className="w-2 h-2 bg-zinc-400 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <div className="p-4 border-t border-zinc-200">
        <div className="flex gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Escribe tu consulta..."
            className="flex-1 px-4 py-3 border-2 border-zinc-200 rounded-xl focus:border-[#90EE90] focus:outline-none transition-colors"
            disabled={loading}
          />
          <button
            onClick={handleSend}
            disabled={!input.trim() || loading}
            className="bg-[#90EE90] text-zinc-900 p-3 rounded-xl hover:bg-[#7CDF7C] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Send size={20} />
          </button>
        </div>
        <p className="text-xs text-zinc-500 mt-2 text-center">
          Powered by Zenex AI
        </p>
      </div>
    </div>
  );
};

export default ChatBot;